import { type Storefront, detectStorefront } from '@/hooks/useStorefront';

/**
 * Payment providers supported by BigFollow.
 */
export type PaymentProvider = 'stripe' | 'twint';

/**
 * Single Source of Truth: which payment methods are available per storefront.
 *
 * CH keeps the exact today-order: ['twint', 'stripe'] — TWINT at index 0
 * remains the default and is rendered first, byte-identical to the legacy UI.
 * All other storefronts offer Stripe only.
 *
 * KEEP IN SYNC with any backend hard-locks (e.g. create-woo-order TWINT guard).
 */
export const PAYMENT_METHODS_BY_STOREFRONT: Record<Storefront, PaymentProvider[]> = {
  ch: ['twint', 'stripe'],
  de: ['stripe'],
  at: ['stripe'],
  it: ['stripe'],
  se: ['stripe'],
  al: ['stripe'],
};

/** Returns the ordered payment-method list for a storefront. */
export function paymentMethodsForStorefront(sf: Storefront): PaymentProvider[] {
  return PAYMENT_METHODS_BY_STOREFRONT[sf] ?? ['stripe'];
}

/** Returns the default (first) payment method for a storefront. */
export function defaultPaymentMethod(sf: Storefront): PaymentProvider {
  return paymentMethodsForStorefront(sf)[0];
}

/** Convenience helper: default method for the currently detected storefront. */
export function defaultPaymentMethodForDetected(): PaymentProvider {
  return defaultPaymentMethod(detectStorefront());
}

/**
 * Human-readable label list of payment methods for a storefront, used by
 * checkout trust badges. Byte-identical to the legacy string for CH.
 */
export function paymentMethodsLabel(sf: Storefront): string {
  const parts = paymentMethodsForStorefront(sf).flatMap((p) => {
    if (p === 'twint') return ['Twint'];
    if (p === 'stripe') return ['Kredit-/Debitkarte', 'Apple Pay', 'Klarna', 'PayPal'];
    return [];
  });
  return parts.join(' · ');
}
